import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import "./GameResults.css";
import "./variables.css";
import API_URL from "./config";
import { getTeamTheme } from "./teamTheme";

const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const shortenName = (fullName) => {
  if (!fullName) return "—";
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  return `${parts[0].charAt(0)}. ${parts.slice(1).join(" ")}`;
};

const POS_CLASSES = ["PG", "SG", "SF", "PF", "C"];

const PosBadge = ({ pos }) => {
  const cls = POS_CLASSES.includes(pos) ? pos : "default";
  return <span className={`pos-badge ${cls}`}>{pos || "?"}</span>;
};

const STAT_COLS = [
  { label: "MIN", key: "minutes" },
  { label: "PTS", key: "points",   key2: true },
  { label: "REB", key: "rebounds", key2: true },
  { label: "AST", key: "assists",  key2: true },
  { label: "STL", key: "steals" },
  { label: "BLK", key: "blocks" },
  { label: "FG",  shooting: ["fieldGoalsMade",      "fieldGoalsAttempted"] },
  { label: "3PT", shooting: ["threePointersMade",   "threePointersAttempted"] },
  { label: "FT",  shooting: ["freeThrowsMade",      "freeThrowsAttempted"] },
];

const PlayerRow = ({ player, isBench }) => (
  <div className={`stat-row${isBench ? " bench-row" : ""}`}>
    <div className="player-cell">
      <PosBadge pos={player.position} />
      <span className="player-name-text">{shortenName(player.playerName)}</span>
    </div>
    {STAT_COLS.map((col) => {
      if (col.shooting) {
        const [made, att] = col.shooting;
        return (
          <span key={col.label} className="stat-value shooting">
            {player[made] ?? 0}/{player[att] ?? 0}
          </span>
        );
      }
      return (
        <span key={col.label} className={`stat-value${col.key2 ? " key" : ""}`}>
          {player[col.key] ?? 0}
        </span>
      );
    })}
  </div>
);

const StatTable = ({ teamName, stats, theme }) => {
  const hasStarterInfo = stats.some((s) => s.isStarter !== undefined && s.isStarter !== null);
  const starters = hasStarterInfo ? stats.filter((s) => s.isStarter) : stats.slice(0, 5);
  const bench    = hasStarterInfo ? stats.filter((s) => !s.isStarter) : stats.slice(5);

  return (
    <div className="stats-box">
      <div className="stats-box-header" style={{ borderBottomColor: hexToRgba(theme.primary, 0.35) }}>
        <div
          className="stats-team-badge"
          style={{ background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})` }}
        >
          {teamName.charAt(0)}
        </div>
        <h2 className="stats-title">{teamName}</h2>
      </div>
      <div className="stats-header">
        <span className="header-player">Player</span>
        {STAT_COLS.map((col) => (
          <span key={col.label} className={`header-stat${col.key2 ? " key-stat" : ""}`}>
            {col.label}
          </span>
        ))}
      </div>
      <div className="stats-list">
        {starters.length > 0 && (
          <>
            <div
              className="stat-section-label"
              style={{ "--section-label-color": hexToRgba(theme.secondary, 0.7) }}
            >
              Starters
            </div>
            {starters.map((p, i) => <PlayerRow key={i} player={p} isBench={false} />)}
          </>
        )}
        {bench.length > 0 && (
          <>
            <div className="stat-section-label">Bench</div>
            {bench.map((p, i) => <PlayerRow key={i} player={p} isBench={true} />)}
          </>
        )}
        {stats.length === 0 && (
          <div className="empty-message">No player stats available.</div>
        )}
      </div>
    </div>
  );
};

function GameDetail() {
  const { resultId } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchResult = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const userId = user._id;
        const activeSaveId = localStorage.getItem("activeSaveId");

        if (!userId || !activeSaveId) {
          setError("Please log in and select a save.");
          setLoading(false);
          return;
        }

        const res = await fetch(
          `${API_URL}/api/saves/${activeSaveId}/games/${resultId}`,
          { headers: { "X-User-ID": userId } }
        );

        const data = await res.json();

        if (!res.ok) {
          setError(data.detail || "Failed to load game result");
          setLoading(false);
          return;
        }

        setResult(data.result);
      } catch (err) {
        setError(err.message || "Server error");
      } finally {
        setLoading(false);
      }
    };

    fetchResult();
  }, [resultId]);

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="game-results-container">
          <p className="results-subtitle">Loading game details...</p>
        </div>
      </>
    );
  }

  if (error || !result) {
    return (
      <>
        <Navbar />
        <div className="game-results-container">
          <p className="results-subtitle">{error || "Game not found."}</p>
          <button className="results-button" onClick={() => navigate(-1)}>
            Go Back
          </button>
        </div>
      </>
    );
  }

  const homeTeamName = result.homeTeamName || "Home Team";
  const awayTeamName = result.awayTeamName || "Away Team";
  const homeScore = result.homeScore ?? 0;
  const awayScore = result.awayScore ?? 0;

  const homeTheme = getTeamTheme(homeTeamName);
  const awayTheme = getTeamTheme(awayTeamName);
  const homeWon = homeScore > awayScore;
  const awayWon = awayScore > homeScore;

  const homeTeamStats = (result.playerStats || []).filter(
    (s) => s.teamId === result.homeTeam
  );
  const awayTeamStats = (result.playerStats || []).filter(
    (s) => s.teamId === result.awayTeam
  );

  return (
    <>
      <Navbar />
      <div className="game-results-container">
        <div className="game-results-header">
          <h1 className="results-title">Game Summary</h1>
          <p className="results-subtitle">
            Season {result.season}
          </p>
        </div>

        {/* Matchup Hero */}
        <div className="matchup-hero">
          {/* Away Team */}
          <div
            className="matchup-team away"
            style={{ "--away-primary-alpha": hexToRgba(awayTheme.primary, 0.18) }}
          >
            <div
              className="team-badge"
              style={{ background: `linear-gradient(135deg, ${awayTheme.primary}, ${awayTheme.secondary})` }}
            >
              {awayTeamName.charAt(0)}
            </div>
            <div className="matchup-team-name">{awayTeamName}</div>
            <div
              className={`matchup-score${awayWon ? " winner" : ""}`}
              style={awayWon ? { color: awayTheme.secondary } : {}}
            >
              {awayScore}
            </div>
            {awayWon && (
              <span
                className="winner-badge"
                style={{
                  background: hexToRgba(awayTheme.primary, 0.25),
                  color: awayTheme.secondary,
                  border: `1px solid ${hexToRgba(awayTheme.secondary, 0.4)}`,
                }}
              >
                WINNER
              </span>
            )}
          </div>

          {/* Center column */}
          <div className="matchup-center">
            <span className="matchup-final-label">Final</span>
            <span className="matchup-vs">VS</span>
            <span className="matchup-meta">
              Season {result.season}
            </span>
          </div>

          {/* Home Team */}
          <div
            className="matchup-team home"
            style={{ "--home-primary-alpha": hexToRgba(homeTheme.primary, 0.18) }}
          >
            <div
              className="team-badge"
              style={{ background: `linear-gradient(135deg, ${homeTheme.primary}, ${homeTheme.secondary})` }}
            >
              {homeTeamName.charAt(0)}
            </div>
            <div className="matchup-team-name">{homeTeamName}</div>
            <div
              className={`matchup-score${homeWon ? " winner" : ""}`}
              style={homeWon ? { color: homeTheme.secondary } : {}}
            >
              {homeScore}
            </div>
            {homeWon && (
              <span
                className="winner-badge"
                style={{
                  background: hexToRgba(homeTheme.primary, 0.25),
                  color: homeTheme.secondary,
                  border: `1px solid ${hexToRgba(homeTheme.secondary, 0.4)}`,
                }}
              >
                WINNER
              </span>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="stats-container">
          <StatTable teamName={awayTeamName} stats={awayTeamStats} theme={awayTheme} />
          <StatTable teamName={homeTeamName} stats={homeTeamStats} theme={homeTheme} />
        </div>

        <button className="results-button" onClick={() => navigate(-1)}>
          Back to Schedule
        </button>
      </div>
    </>
  );
}

export default GameDetail;
